import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Input, Card, Typography, message, Row, Col,
  Tag, Tooltip, Popconfirm, Select,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ProjectOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const ProjeSatisListe = () => {
  const [data, setData] = useState([]);
  const [projeler, setProjeler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterProje, setFilterProje] = useState(null);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (!isAdmin()) params.append('sadeceBenim', 'true');
      if (filterProje) params.append('projeId', filterProje);
      const res = await api.get(`/projesatis?${params}`);
      const arr = Array.isArray(res.data) ? res.data : res.data.data || [];
      setData(arr);
    } catch {
      message.error('Proje satışları yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, filterProje]);

  const fetchProjeler = useCallback(async () => {
    try {
      const res = await api.get('/projeler');
      const arr = Array.isArray(res.data) ? res.data : res.data.data || [];
      setProjeler(arr);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchProjeler();
  }, [fetchProjeler]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/projesatis/${id}`);
      message.success('Kayıt silindi');
      fetchData();
    } catch {
      message.error('Silme başarısız');
    }
  };

  const handleWordIndir = async (record) => {
    try {
      const response = await api.get(`/projesatis/generate-word/${record.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ProjeSozlesmesi_${record.projeAdi || record.id}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Word belgesi oluşturulamadı');
    }
  };

  const formatMoney = (val) => {
    if (!val && val !== 0) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val);
  };

  const filtered = data.filter((item) => {
    const q = searchText.toLowerCase();
    return (
      (item.aliciAdi || '').toLowerCase().includes(q) ||
      (item.sozlesmeNo || '').toLowerCase().includes(q) ||
      (item.projeAdi || '').toLowerCase().includes(q)
    );
  });

  const durumColors = { 'Aktif': 'green', 'İptal': 'red', 'Tamamlandı': 'blue' };

  const columns = [
    {
      title: 'Proje',
      dataIndex: 'projeAdi',
      key: 'projeAdi',
      render: (t) => <Tag color="purple">{t || '-'}</Tag>,
    },
    {
      title: 'Blok / Daire / Kat',
      key: 'birim',
      render: (_, r) => `${r.blokNo || '-'} / ${r.daireNo || '-'} / ${r.katNo || '-'}`,
    },
    {
      title: 'Alıcı',
      dataIndex: 'aliciAdi',
      key: 'aliciAdi',
      render: (t) => <strong>{t || '-'}</strong>,
    },
    {
      title: 'Satış Bedeli',
      dataIndex: 'satisBedeli',
      key: 'satisBedeli',
      render: (v) => <span className="money-value">{formatMoney(v)}</span>,
    },
    {
      title: 'Peşinat',
      dataIndex: 'pesinat',
      key: 'pesinat',
      render: (v) => formatMoney(v),
    },
    {
      title: 'Sözleşme No',
      dataIndex: 'sozlesmeNo',
      key: 'sozlesmeNo',
    },
    {
      title: 'Tarih',
      dataIndex: 'sozlesmeTarihi',
      key: 'sozlesmeTarihi',
      render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Durum',
      dataIndex: 'durum',
      key: 'durum',
      render: (v) => v ? <Tag color={durumColors[v] || 'default'}>{v}</Tag> : '-',
    },
    {
      title: 'Oluşturan',
      dataIndex: 'olusturanKullanici',
      key: 'olusturanKullanici',
      render: (v) => v || '-',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Düzenle">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/proje-satis/${record.id}/duzenle`)}
            />
          </Tooltip>
          <Tooltip title="Word İndir">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleWordIndir(record)}
            />
          </Tooltip>
          <Tooltip title="Sil">
            <Popconfirm
              title="Bu kaydı silmek istiyor musunuz?"
              onConfirm={() => handleDelete(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <ProjectOutlined style={{ fontSize: 20, color: '#1677ff' }} />
            <Title level={4} style={{ margin: 0 }}>Proje Satışları</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Proje filtrele"
              allowClear
              style={{ width: 200 }}
              onChange={(v) => setFilterProje(v)}
              value={filterProje}
            >
              {projeler.map((p) => (
                <Option key={p.id} value={p.id}>{p.projeAdi}</Option>
              ))}
            </Select>
            <Search
              placeholder="Alıcı veya sözleşme no ara..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 240 }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/proje-satis/yeni')}>
              Yeni Kayıt
            </Button>
          </Space>
        </Col>
      </Row>

      <Card className="content-card" bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          loading={loading}
          bordered
          size="middle"
          scroll={{ x: 1000 }}
          pagination={{ pageSize: 15, showTotal: (t) => `Toplam ${t} kayıt`, showSizeChanger: true }}
          locale={{ emptyText: 'Proje satışı bulunamadı' }}
        />
      </Card>
    </div>
  );
};

export default ProjeSatisListe;
